/**
 * useLocale — 语言切换 composable
 * - 从 localStorage 读取持久化语言设置
 * - setLocale 写入 localStorage + 更新 html[lang]
 * - 仅暴露 enabled=true 的语言给 UI
 * R3: localStorage 读写仅 debug 级，不 log 敏感信息
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from '@/i18n/index'
import type { SupportedLocale } from '@/i18n/index'

const STORAGE_KEY = 'kiosk_locale'

/** 验证字符串是否为合法 SupportedLocale */
function isValidLocale(s: string): s is SupportedLocale {
  return SUPPORTED_LOCALES.some(l => l.code === s)
}

export function useLocale() {
  const { locale } = useI18n()

  /**
   * 初始化语言：优先读 localStorage，读不到则用默认值
   * 应在 App.vue setup() 最顶部调用一次
   */
  const initLocale = (): void => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored && isValidLocale(stored)) {
        locale.value = stored
      } else {
        locale.value = DEFAULT_LOCALE
      }
    } catch {
      // localStorage 不可用（如 SSR / 隐私模式拒绝访问）
      locale.value = DEFAULT_LOCALE
    }
    // 同步 html[lang]
    document.documentElement.lang = locale.value
  }

  /**
   * 切换语言
   * - 非法值 / disabled 语言：忽略
   * - 成功：写 localStorage + 更新 html[lang]
   */
  const setLocale = (newLocale: SupportedLocale): void => {
    if (!isValidLocale(newLocale)) return

    const meta = SUPPORTED_LOCALES.find(l => l.code === newLocale)
    if (!meta || !meta.enabled) {
      // R3: 不输出用户敏感信息
      console.warn(`[i18n] locale "${newLocale}" is not enabled, ignoring`)
      return
    }

    locale.value = newLocale

    try {
      localStorage.setItem(STORAGE_KEY, newLocale)
    } catch {
      // ignore write failure（隐私模式 / storage full）
    }

    document.documentElement.lang = newLocale
  }

  /** 当前语言代码 */
  const currentLocale = computed(() => locale.value as SupportedLocale)

  /** UI 可用语言列表（仅 enabled=true） */
  const availableLocales = computed(() => SUPPORTED_LOCALES.filter(l => l.enabled))

  return {
    currentLocale,
    availableLocales,
    setLocale,
    initLocale,
  }
}
